import { useMaquetesTheme } from '../context/MaquetesThemeContext';
import LiquidText, { liquidTone } from './LiquidText';
import React, { useState, useEffect, useRef, useId } from 'react';
import api from '../services/api';

/* ============================================================
   RESOLVER UM PRAZO A PARTIR DO PAINEL ⏰ Validades

   Duas janelas: "Resolver" (data da resolução + evidência) e
   "Definir prazo" (para o que está sem data). O painel não guarda
   nada seu: o servidor grava no módulo de origem, no mesmo sítio
   onde se gravaria indo ao separador, para o painel e o separador
   dizerem o mesmo e a auditora encontrar a evidência onde a procura.

   Os campos vêm do servidor (item.resolver.campos) e são desenhados
   aqui só pelo tipo — um tipo de resolução novo não obriga a mexer
   neste ficheiro. As únicas regras que se repetem aqui são as que
   poupam a quem está a enviar um ficheiro grande a espera por uma
   recusa certa: campos obrigatórios, data da resolução no futuro,
   validade antes do documento e o "não medido" sem justificação.
   O servidor volta a verificar tudo.
   ============================================================ */

// Só uma sugestão ao explorador de ficheiros: PDF, imagens e Office. Quem
// decide é o servidor (fileFilterSeguro). Não se usa image/* porque apanha o
// SVG, que o servidor recusa.
// E-mails e textos também: no plano de comunicação a evidência é muitas vezes
// um e-mail, e os ecrãs de origem não filtram nada.
export const FICHEIROS_ACEITES = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tif,.tiff,.heic,'
    + '.doc,.docx,.xls,.xlsx,.xlsm,.ppt,.pptx,.odt,.ods,.odp,.odg,'
    + '.msg,.eml,.txt,.csv,.rtf,.zip';

// Igual a ObjetivosIndicadores.jsx: um período fechado sem medição tem este
// valor, e a razão é obrigatória — é ela que o distingue de um esquecimento.
const NAO_MEDIDO = 'não medido';
const eNaoMedido = (v) => ['não medido', 'nao medido'].includes(String(v || '').trim().toLowerCase());

// Uma ligação lenta (acesso de fora, pelo DDNS) com um ficheiro de 25 MB
// passa facilmente dos 15 s por omissão do api.js.
const TEMPO_ENVIO = 300000;

const dd = (n) => String(n).padStart(2, '0');
// O dia de hoje na hora local. O toISOString dava o dia em UTC: entre a
// meia-noite e a uma da manhã (hora de verão) ainda era ontem.
export const hojeLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${dd(d.getMonth() + 1)}-${dd(d.getDate())}`;
};
export const somaDias = (iso, n) => {
    const [a, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10);
};
// O campo de data devolve '' quando a data está incompleta, mas um valor
// escrito à mão (ou vindo do servidor) pode chegar como 2026-02-30.
const dataValida = (s) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s || '')) return false;
    const [a, m, d] = s.split('-').map(Number);
    const x = new Date(Date.UTC(a, m - 1, d));
    return x.getUTCFullYear() === a && x.getUTCMonth() === m - 1 && x.getUTCDate() === d;
};
const fmtData = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');
const fmtTamanho = (b) => (b >= 1048576
    ? `${(b / 1048576).toFixed(1).replace('.', ',')} MB`
    : `${Math.max(1, Math.round(b / 1024))} KB`);
const textoPrazo = (item) => {
    if (!item.prazo) return 'Sem prazo definido';
    const d = item.dias;
    const rel = d === null || d === undefined ? ''
        : d === 0 ? 'vence hoje'
            : d < 0 ? (d === -1 ? 'venceu há 1 dia' : `venceu há ${-d} dias`)
                : (d === 1 ? 'falta 1 dia' : `faltam ${d} dias`);
    return `Prazo ${fmtData(item.prazo)}${rel ? ` — ${rel}` : ''}`;
};

/* O erro do servidor vem em português. O que não vem dele (rede, tempo
   esgotado, limite de tamanho do multer, rota inexistente) traduz-se aqui,
   para não aparecer "Not Found" ou "File too large" a quem está a trabalhar. */
export const mensagemErro = (e, porOmissao) => {
    const r = e?.response;
    if (!r) {
        if (e?.code === 'ECONNABORTED' || e?.code === 'ETIMEDOUT') {
            return 'O servidor demorou demasiado a responder. A gravação pode ter ficado feita: '
                + 'feche esta janela e atualize o painel antes de tentar outra vez.';
        }
        return 'Sem ligação ao servidor. Verifique a rede e tente outra vez.';
    }
    const msg = typeof r.data?.error === 'string' ? r.data.error : '';
    if (r.status === 413 || /too large/i.test(msg)) return 'O ficheiro é demasiado grande para ser guardado.';
    if (r.status === 404 && (!msg || msg === 'Not Found')) {
        return 'Não foi possível guardar: o registo já não existe, ou esta opção ainda não está disponível no servidor.';
    }
    return msg || porOmissao;
};

/* ---------- janela ---------- */

const FOCAVEIS = 'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/* A moldura das duas janelas: o mesmo overlay e a mesma caixa das outras
   janelas do Sistema Integrado. O foco entra no primeiro campo, fica preso
   na janela enquanto está aberta e volta ao botão que a abriu. Esc fecha,
   exceto a meio de um envio (fechar aí não cancelava a gravação e o
   utilizador ficava sem saber o resultado). Clicar fora só fecha se não
   houver nada escrito — perder um ficheiro escolhido por um clique ao lado
   era pior do que ter de carregar em Cancelar. */
function Janela({ titulo, larguraMax = 600, ocupado, sujo, onFechar, children }) {
    const {theme,active}=useMaquetesTheme();
    const idTitulo = useId();
    const caixa = useRef(null);
    const premidoNoFundo = useRef(false);
    const estado = useRef({ ocupado, onFechar });
    estado.current = { ocupado, onFechar };

    useEffect(() => {
        const antes = document.activeElement;
        const el = caixa.current;
        const primeiro = el?.querySelector('[data-primeiro]') || el?.querySelector(FOCAVEIS);
        primeiro?.focus();

        const aoTeclar = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                if (!estado.current.ocupado) estado.current.onFechar();
                return;
            }
            if (e.key !== 'Tab' || !el) return;
            const lista = [...el.querySelectorAll(FOCAVEIS)].filter(x => x.offsetParent !== null || x === document.activeElement);
            if (!lista.length) return;
            const pri = lista[0];
            const ult = lista[lista.length - 1];
            if (!el.contains(document.activeElement)) { e.preventDefault(); pri.focus(); }
            else if (e.shiftKey && document.activeElement === pri) { e.preventDefault(); ult.focus(); }
            else if (!e.shiftKey && document.activeElement === ult) { e.preventDefault(); pri.focus(); }
        };
        document.addEventListener('keydown', aoTeclar);
        return () => {
            document.removeEventListener('keydown', aoTeclar);
            if (antes && typeof antes.focus === 'function' && document.contains(antes)) antes.focus();
        };
    }, []);

    return (
        // Só fecha se o clique começou e acabou no fundo: selecionar texto num
        // campo e largar o rato fora da caixa não pode fechar a janela.
        <div data-liquid-dialog="true" data-sgi-dialog="true" data-orc-theme={active ? theme : undefined} style={overlay}
            onMouseDown={e => { premidoNoFundo.current = e.target === e.currentTarget; }}
            onClick={e => {
                if (e.target === e.currentTarget && premidoNoFundo.current && !ocupado && !sujo) onFechar();
                premidoNoFundo.current = false;
            }}>
            <div className="liquid-dialog-card" ref={caixa} role="dialog" aria-modal="true" aria-labelledby={idTitulo}
                style={{ ...modal, maxWidth: larguraMax }}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 id={idTitulo} style={{ margin: 0, fontSize: '1.08rem', color: '#1e293b' }}><LiquidText>{titulo}</LiquidText></h3>
                    <button data-liquid-secondary="true" type="button" onClick={onFechar} disabled={ocupado} aria-label="Fechar"
                        style={{ ...closeBtn, opacity: ocupado ? 0.4 : 1 }}><LiquidText>✕</LiquidText></button>
                </div>
                {children}
            </div>
        </div>
    );
}

// A situação que se está a tratar, tal como aparece na linha do painel.
function Situacao({ item, etiquetaEstado }) {
    return (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                {etiquetaEstado}
                <span style={{ fontSize: '0.76rem', color: '#64748b' }}>
                    {[item.separador_label, item.tipo, item.ref].filter(Boolean).join(' · ')}
                </span>
            </div>
            <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.92rem' }}>{item.descricao || '—'}</div>
            <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: 3 }}>{textoPrazo(item)}</div>
        </div>
    );
}

function Destino({ texto }) {
    if (!texto) return null;
    return (
        <div data-liquid-notice="true" data-liquid-tone={liquidTone('#eef2ff')} style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '8px 12px', marginBottom: 14, color: '#3730a3', fontSize: '0.84rem' }}>
            <span style={{ ...lbl, color: '#4f46e5', marginBottom: 2 }}>Onde fica registado</span>
            <LiquidText>{texto}</LiquidText>
        </div>
    );
}

const Asterisco = () => <span aria-hidden="true" style={{ color: '#d03b3b', marginLeft: 2 }}>*</span>;

function ErroGeral({ texto }) {
    if (!texto) return null;
    return (
        <div data-liquid-notice="true" data-liquid-tone={liquidTone('#fdeeee')} role="alert" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fdeeee', border: '1px solid #f3c4c4', color: '#9b2c2c', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: '0.85rem' }}>
            <span aria-hidden="true" style={{ fontWeight: 800 }}>!</span>
            <span><LiquidText>{texto}</LiquidText></span>
        </div>
    );
}

/* ---------- Resolver ---------- */

// O contrato garante 'data' e 'observacao' em todos os tipos; se um tipo novo
// chegar sem eles, acrescentam-se, porque o servidor pede-os sempre.
const normalizarCampos = (campos) => {
    const lista = Array.isArray(campos) ? campos.filter(c => c && c.nome) : [];
    if (!lista.some(c => c.nome === 'data')) {
        lista.unshift({ nome: 'data', tipo: 'date', rotulo: 'Data da resolução', obrigatorio: true });
    }
    if (!lista.some(c => c.nome === 'observacao')) {
        lista.push({ nome: 'observacao', tipo: 'textarea', rotulo: 'Observação', obrigatorio: false });
    }
    return lista;
};

// Estes nomes são do próprio pedido; um campo com o mesmo nome estragava-o.
const RESERVADOS = new Set(['tipo', 'id', 'extra']);

export function ResolverPrazoModal({ item, etiquetaEstado, onFechar, onResolvido, onFalhou }) {
    const r = item.resolver;
    const base = useId();
    const [hoje] = useState(hojeLocal);
    const [campos] = useState(() => normalizarCampos(r.campos));

    /* valor_inicial pode ser uma data, "hoje", ou o nome de outro campo
       (a data do documento começa igual à data da resolução). No último caso
       o campo acompanha o outro até ser mexido à mão. */
    const [segue] = useState(() => Object.fromEntries(campos
        .filter(c => c.nome !== 'data' && c.valor_inicial && campos.some(o => o.nome === c.valor_inicial && o.nome !== c.nome))
        .map(c => [c.nome, c.valor_inicial])));
    const [valores, setValores] = useState(() => {
        const v = {};
        const inicial = (c) => {
            const vi = c.valor_inicial;
            // Sem proposta do servidor, a data fica vazia: ele só não propõe hoje
            // quando hoje não serve (plano fora da janela, auditoria por começar).
            if (c.nome === 'data') return dataValida(vi) && vi <= hoje ? vi : '';
            if (vi === 'hoje') return hoje;
            return vi === undefined || vi === null ? '' : String(vi);
        };
        campos.filter(c => c.tipo !== 'file').forEach(c => { v[c.nome] = inicial(c); });
        Object.entries(segue).forEach(([nome, de]) => { v[nome] = v[de] ?? ''; });
        return v;
    });
    const [ficheiros, setFicheiros] = useState({});
    const [tocados, setTocados] = useState({});
    const [errosCampo, setErrosCampo] = useState({});
    const [erro, setErro] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [progresso, setProgresso] = useState(null);
    const aEnviar = useRef(false);
    const caixaForm = useRef(null);

    const idDe = (nome) => `${base}-${nome}`;
    const naoMedido = r.tipo === 'medicao_objetivo' && eNaoMedido(valores.valor);
    const obrigatorio = (c) => !!c.obrigatorio || (c.nome === 'observacao' && naoMedido);
    const sujo = Object.keys(tocados).length > 0;

    const mudar = (nome, valor) => {
        setValores(prev => {
            const novo = { ...prev, [nome]: valor };
            Object.entries(segue).forEach(([n, de]) => { if (de === nome && !tocados[n]) novo[n] = valor; });
            return novo;
        });
        setTocados(prev => (prev[nome] ? prev : { ...prev, [nome]: true }));
        setErrosCampo(prev => (prev[nome] ? { ...prev, [nome]: '' } : prev));
    };
    const escolherFicheiro = (nome, f) => {
        setFicheiros(prev => ({ ...prev, [nome]: f || null }));
        setTocados(prev => ({ ...prev, [nome]: true }));
        setErrosCampo(prev => (prev[nome] ? { ...prev, [nome]: '' } : prev));
    };

    const validar = () => {
        const e = {};
        campos.forEach(c => {
            if (c.tipo === 'file') {
                if (obrigatorio(c) && !ficheiros[c.nome]) e[c.nome] = 'Escolha o ficheiro com a evidência.';
                return;
            }
            const v = String(valores[c.nome] ?? '').trim();
            if (!v) {
                if (obrigatorio(c)) {
                    e[c.nome] = c.tipo === 'date' ? 'Indique a data completa (dia, mês e ano).'
                        : c.tipo === 'select' ? 'Escolha uma opção.' : 'Preencha este campo.';
                }
                return;
            }
            if (c.tipo === 'date' && !dataValida(v)) e[c.nome] = 'Esta data não existe.';
            else if (c.nome === 'data' && v > hoje) e[c.nome] = 'A data da resolução não pode ser no futuro.';
            else if (c.tipo === 'select' && Array.isArray(c.opcoes) && !c.opcoes.some(o => String(o.valor) === v)) {
                e[c.nome] = 'Escolha uma das opções.';
            }
        });
        // Renovar uma evidência: um documento que caduca antes de ser emitido
        // é quase sempre a validade escrita no campo errado.
        const temDatas = campos.some(c => c.nome === 'validade') && campos.some(c => c.nome === 'data_documento');
        if (temDatas && !e.validade && !e.data_documento && valores.validade && valores.data_documento
            && valores.validade <= valores.data_documento) {
            e.validade = 'A validade tem de ser depois da data do documento.';
        }
        if (naoMedido && !String(valores.observacao || '').trim()) {
            e.observacao = 'Escreva porque é que o período não tem medição.';
        }
        return e;
    };

    const guardar = async (ev) => {
        ev.preventDefault();
        if (aEnviar.current) return;
        const e = validar();
        const comErro = campos.filter(c => e[c.nome]);
        setErrosCampo(e);
        if (comErro.length) {
            setErro('');
            document.getElementById(idDe(comErro[0].nome))?.focus();
            return;
        }
        aEnviar.current = true;
        setOcupado(true); setErro(''); setProgresso(null);

        /* O ficheiro vai no fim: o multer só conhece, quando grava o ficheiro,
           os campos de texto que chegaram antes dele — e o servidor pode
           precisar do tipo e do id para escolher a pasta. */
        const fd = new FormData();
        fd.append('tipo', r.tipo);
        fd.append('id', String(r.id));
        if (r.extra !== null && r.extra !== undefined) fd.append('extra', JSON.stringify(r.extra));
        campos.forEach(c => {
            if (c.tipo === 'file' || RESERVADOS.has(c.nome)) return;
            const v = valores[c.nome] ?? '';
            fd.append(c.nome, c.tipo === 'text' || c.tipo === 'textarea' ? String(v).trim() : String(v));
        });
        campos.forEach(c => {
            if (c.tipo === 'file' && ficheiros[c.nome]) fd.append(c.nome, ficheiros[c.nome]);
        });
        const comFicheiro = campos.some(c => c.tipo === 'file' && ficheiros[c.nome]);

        try {
            // Sem Content-Type à mão: o api.js retira-o para FormData e o browser
            // põe o multipart com a fronteira certa.
            const resp = await api.post('/sistema-integrado/prazos/resolver', fd, {
                timeout: TEMPO_ENVIO,
                onUploadProgress: comFicheiro
                    ? (p) => { if (p.total) setProgresso(Math.min(100, Math.round((p.loaded * 100) / p.total))); }
                    : undefined,
            });
            aEnviar.current = false;
            onResolvido(resp.data?.mensagem || 'Resolução guardada.');
        } catch (err) {
            aEnviar.current = false;
            setOcupado(false); setProgresso(null);
            setErro(mensagemErro(err, 'Não foi possível guardar a resolução.'));
            // A lista pode ter mudado entretanto (alguém resolveu o mesmo
            // item noutro posto): o painel atualiza-se por trás da janela.
            onFalhou?.();
            caixaForm.current?.scrollIntoView?.({ block: 'nearest' });
        }
    };

    const campo = (c, primeiro) => {
        const id = idDe(c.nome);
        const idAjuda = `${id}-ajuda`;
        const idErro = `${id}-erro`;
        const msgErro = errosCampo[c.nome];
        // Um ficheiro opcional leva sempre uma nota a dizer que pode faltar.
        const temAjuda = !!c.ajuda || (c.tipo === 'file' && !obrigatorio(c));
        const descr = [temAjuda ? idAjuda : '', msgErro ? idErro : ''].filter(Boolean).join(' ') || undefined;
        const comum = {
            id,
            name: c.nome,
            disabled: ocupado,
            'aria-required': obrigatorio(c) || undefined,
            'aria-invalid': msgErro ? true : undefined,
            'aria-describedby': descr,
            'data-primeiro': primeiro ? '' : undefined,
        };
        const borda = msgErro ? { borderColor: '#d03b3b' } : null;
        // A toda a largura: texto corrido, ficheiro, e o que a meia largura
        // ficava cortado — escolhas com opções compridas e títulos.
        const opcaoMaisLonga = Math.max(0, ...(c.opcoes || []).map(o => String(o.rotulo ?? o.valor).length));
        const largo = c.tipo === 'textarea' || c.tipo === 'file'
            || (c.tipo === 'select' && opcaoMaisLonga > 30)
            || (c.tipo === 'text' && (String(c.valor_inicial ?? '').length > 24 || /titulo|participantes|descricao/.test(c.nome)));
        let controlo;

        if (c.tipo === 'date') {
            let min;
            let max;
            if (c.nome === 'data') max = hoje;
            if (c.nome === 'validade' && dataValida(valores.data_documento)) min = somaDias(valores.data_documento, 1);
            controlo = (
                <input {...comum} type="date" value={valores[c.nome] ?? ''} min={min} max={max}
                    onChange={e => mudar(c.nome, e.target.value)}
                    style={{ ...inp, ...borda, width: '100%' }} />
            );
        } else if (c.tipo === 'select') {
            controlo = (
                <select {...comum} value={valores[c.nome] ?? ''} onChange={e => mudar(c.nome, e.target.value)}
                    style={{ ...inp, ...borda, width: '100%' }}>
                    <option value="">— escolher —</option>
                    {(c.opcoes || []).map(o => <option key={String(o.valor)} value={String(o.valor)}>{o.rotulo ?? o.valor}</option>)}
                </select>
            );
        } else if (c.tipo === 'textarea') {
            controlo = (
                <textarea {...comum} rows={3} value={valores[c.nome] ?? ''} onChange={e => mudar(c.nome, e.target.value)}
                    placeholder={c.nome === 'observacao' && naoMedido ? 'ex.: a fonte de informação não produziu o valor; indicador não calculável com a fórmula em vigor' : undefined}
                    style={{ ...inp, ...borda, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
            );
        } else if (c.tipo === 'file') {
            const f = ficheiros[c.nome];
            controlo = (
                <div style={{ border: `1px dashed ${msgErro ? '#d03b3b' : '#cbd5e1'}`, borderRadius: 8, padding: '8px 10px', background: '#fff' }}>
                    <input {...comum} type="file" accept={FICHEIROS_ACEITES}
                        onChange={e => escolherFicheiro(c.nome, e.target.files?.[0])}
                        style={{ fontSize: '0.84rem', maxWidth: '100%' }} />
                    {f && (
                        <div style={{ fontSize: '0.78rem', color: '#166534', marginTop: 5 }}>
                            <span aria-hidden="true"><LiquidText>✓ </LiquidText></span>{f.name} · {fmtTamanho(f.size)}
                        </div>
                    )}
                </div>
            );
        } else {
            controlo = (
                <input {...comum} type="text" value={valores[c.nome] ?? ''} onChange={e => mudar(c.nome, e.target.value)}
                    style={{ ...inp, ...borda, width: '100%' }} />
            );
        }

        return (
            <div key={c.nome} style={{ gridColumn: largo ? '1 / -1' : undefined, minWidth: 0 }}>
                <label htmlFor={id} style={lbl}>
                    {c.rotulo || c.nome}
                    {obrigatorio(c) ? <Asterisco /> : <span style={{ textTransform: 'none', fontWeight: 500, color: '#94a3b8' }}> (opcional)</span>}
                </label>
                {controlo}
                {/* Medição do objetivo: o período pode fechar sem valor, como no
                    ecrã dos Objetivos — com a razão escrita na observação. */}
                {r.tipo === 'medicao_objetivo' && c.nome === 'valor' && c.tipo === 'text' && !naoMedido
                    && item.prazo && item.prazo < hoje && (
                    <button type="button" disabled={ocupado} onClick={() => {
                        mudar('valor', NAO_MEDIDO);
                        setTimeout(() => document.getElementById(idDe('observacao'))?.focus(), 0);
                    }} style={{ ...btnLink, marginTop: 4 }}>
                        Não foi possível medir este período
                    </button>
                )}
                {r.tipo === 'medicao_objetivo' && c.nome === 'valor' && naoMedido && (
                    <div style={{ ...ajuda, color: '#8a5a00' }}>
                        O período fica registado como “{NAO_MEDIDO}” — conta como tratado, não como medido. A observação passa a ser obrigatória.
                    </div>
                )}
                {temAjuda && (
                    <div id={idAjuda} style={ajuda}>
                        {c.ajuda || 'Pode guardar sem ficheiro, mas com ele a resolução fica documentada.'}
                    </div>
                )}
                {msgErro && <div id={idErro} style={estErro}>{msgErro}</div>}
            </div>
        );
    };

    return (
        <Janela titulo={r.rotulo || 'Resolver'} ocupado={ocupado} sujo={sujo} onFechar={onFechar}>
            <Situacao item={item} etiquetaEstado={etiquetaEstado} />
            <Destino texto={r.destino} />
            <form ref={caixaForm} onSubmit={guardar} noValidate>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '12px 14px', marginBottom: 14 }}>
                    {campos.map((c, i) => campo(c, i === 0))}
                </div>
                <div style={{ fontSize: '0.74rem', color: '#94a3b8', marginBottom: 10 }}>
                    <span aria-hidden="true" style={{ color: '#d03b3b' }}>*</span> obrigatório
                </div>
                <ErroGeral texto={erro} />
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {ocupado && (
                        <span role="status" style={{ fontSize: '0.8rem', color: '#64748b', marginRight: 'auto' }}>
                            {progresso !== null && progresso < 100 ? `A enviar o ficheiro… ${progresso}%` : 'A gravar no registo de origem…'}
                        </span>
                    )}
                    <button data-liquid-secondary="true" type="button" onClick={onFechar} disabled={ocupado} style={{ ...btnSec, opacity: ocupado ? 0.6 : 1 }}>Cancelar</button>
                    <button data-liquid-primary="true" type="submit" disabled={ocupado} style={{ ...btnPri, opacity: ocupado ? 0.6 : 1, cursor: ocupado ? 'wait' : 'pointer' }}>
                        {ocupado ? 'A guardar…' : 'Guardar resolução'}
                    </button>
                </div>
            </form>
        </Janela>
    );
}

/* ---------- Definir prazo ---------- */

// Onde cada tipo guarda o prazo — dito antes de gravar. Se o servidor
// mandar a frase (definir_prazo.destino), vale a dele.
const DESTINO_PRAZO = {
    nc: (item) => `Fica como prazo da ação na não conformidade${item.ref ? ` ${item.ref}` : ''}, no separador Não Conformidades.`,
    obrigacao_legal: (item, quando) => `Fica escrito nas evidências da obrigação como “[REVER EM ${quando}]” — é essa marca que o painel lê. Se já houver uma, é substituída.`,
    objetivo: (item) => `Fica como prazo do objetivo${item.ref ? ` ${item.ref}` : ''}, no separador Objetivos.`,
};

export function DefinirPrazoModal({ item, etiquetaEstado, onFechar, onDefinido, onFalhou }) {
    const dp = item.definir_prazo;
    const id = useId();
    const [amanha] = useState(() => somaDias(hojeLocal(), 1));
    const [prazo, setPrazo] = useState('');
    const [erroCampo, setErroCampo] = useState('');
    const [erro, setErro] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const aEnviar = useRef(false);

    const quando = dataValida(prazo) ? `${prazo.slice(8, 10)}-${prazo.slice(5, 7)}-${prazo.slice(0, 4)}` : 'dd-mm-aaaa';
    const destino = dp.destino || DESTINO_PRAZO[dp.tipo]?.(item, quando) || '';

    const guardar = async (ev) => {
        ev.preventDefault();
        if (aEnviar.current) return;
        let msg = '';
        if (!prazo) msg = 'Indique a data completa (dia, mês e ano).';
        else if (!dataValida(prazo)) msg = 'Esta data não existe.';
        else if (prazo < amanha) msg = 'O prazo tem de ser a partir de amanhã.';
        setErroCampo(msg);
        if (msg) { setErro(''); document.getElementById(id)?.focus(); return; }

        aEnviar.current = true;
        setOcupado(true); setErro('');
        try {
            const resp = await api.post('/sistema-integrado/prazos/definir-prazo', { tipo: dp.tipo, id: dp.id, prazo });
            aEnviar.current = false;
            onDefinido(resp.data?.mensagem || `Prazo definido para ${fmtData(prazo)}.`);
        } catch (err) {
            aEnviar.current = false;
            setOcupado(false);
            setErro(mensagemErro(err, 'Não foi possível guardar o prazo.'));
            onFalhou?.();
        }
    };

    return (
        <Janela titulo={dp.rotulo || 'Definir prazo'} larguraMax={460} ocupado={ocupado} sujo={!!prazo} onFechar={onFechar}>
            <Situacao item={item} etiquetaEstado={etiquetaEstado} />
            <Destino texto={destino} />
            <form onSubmit={guardar} noValidate>
                <label htmlFor={id} style={lbl}>Novo prazo<Asterisco /></label>
                <input id={id} data-primeiro="" type="date" value={prazo} min={amanha} disabled={ocupado}
                    aria-required="true" aria-invalid={erroCampo ? true : undefined}
                    aria-describedby={`${id}-ajuda${erroCampo ? ` ${id}-erro` : ''}`}
                    // O erro do servidor era sobre a data anterior; com outra
                    // data deixa de se aplicar.
                    onChange={e => { setPrazo(e.target.value); setErroCampo(''); setErro(''); }}
                    // O Chrome não submete o formulário com Enter num campo de
                    // data. Aqui é o único campo, e Enter é o que se espera.
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !ocupado) { e.preventDefault(); e.currentTarget.form?.requestSubmit?.(); }
                    }}
                    style={{ ...inp, width: 200, ...(erroCampo ? { borderColor: '#d03b3b' } : null) }} />
                <div id={`${id}-ajuda`} style={ajuda}>A partir de amanhã ({fmtData(amanha)}).</div>
                {erroCampo && <div id={`${id}-erro`} style={estErro}>{erroCampo}</div>}
                <div style={{ height: 14 }} />
                <ErroGeral texto={erro} />
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button data-liquid-secondary="true" type="button" onClick={onFechar} disabled={ocupado} style={{ ...btnSec, opacity: ocupado ? 0.6 : 1 }}>Cancelar</button>
                    <button data-liquid-primary="true" type="submit" disabled={ocupado} style={{ ...btnPri, opacity: ocupado ? 0.6 : 1, cursor: ocupado ? 'wait' : 'pointer' }}>
                        {ocupado ? 'A guardar…' : 'Guardar prazo'}
                    </button>
                </div>
            </form>
        </Janela>
    );
}

// Os mesmos estilos das janelas do Sistema Integrado (Formação, Objetivos,
// Requisitos Legais) e dos campos do painel.
const lbl = { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 };
// A borda em três propriedades: com o atalho "border", tirar a cor de erro
// deixava o campo com a cor por omissão do browser.
const inp = { padding: '8px 10px', borderWidth: 1, borderStyle: 'solid', borderColor: '#cbd5e1', borderRadius: 8, fontSize: '0.9rem', background: '#fff', boxSizing: 'border-box', color: '#1e293b' };
const ajuda = { fontSize: '0.75rem', color: '#64748b', marginTop: 4 };
const estErro = { fontSize: '0.78rem', color: '#9b2c2c', marginTop: 4, fontWeight: 600 };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 };
const btnLink = { border: 'none', background: 'none', padding: 0, color: '#4338ca', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.76rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', boxSizing: 'border-box' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
